import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SpeechRecognitionService {
  public isListening = false;
  public transcript = '';
  public transcript$ = new BehaviorSubject<string>('');
  timeoutEvent = new EventEmitter<boolean>();
  private recognition: any;
  private silenceTimeout: any;
  private readonly silenceTimeoutDuration = 2000; // 2 seconds

  constructor(private zone: NgZone) {
    // Check for browser support (Chrome uses webkitSpeechRecognition, standard is SpeechRecognition)
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('Speech Recognition API is not supported in this browser.');
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = navigator.language || 'en-US';

    this.recognition.onresult = (event: any) => {
      this.zone.run(() => {
        this.transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join('');
        this.transcript$.next(this.transcript);
        this.resetSilenceTimeout();
      });
    };

    this.recognition.onerror = (event: any) => {
      console.error(event.error);
    };

    this.recognition.onend = () => {
      this.isListening = false;
      clearTimeout(this.silenceTimeout);
    };
  }

  setLanguage(language: string): void {
    if (!this.recognition) {
      return;
    }
    this.recognition.lang = language;
  }

  startListening(): void {
    if (!this.recognition) {
      console.warn('Speech Recognition is not available in this browser.');
      return;
    }
    if (!this.isListening) {
      this.recognition.start();
      this.isListening = true;
      this.resetSilenceTimeout();
    }
  }

  stopListening(): void {
    if (!this.recognition) {
      return;
    }
    if (this.isListening) {
      this.recognition.stop();
      this.isListening = false;
      clearTimeout(this.silenceTimeout);
      this.timeoutEvent.emit(true);
    }
  }

  isSupported(): boolean {
    return !!this.recognition;
  }

  private resetSilenceTimeout(): void {
    clearTimeout(this.silenceTimeout);
    this.silenceTimeout = setTimeout(() => {
      this.stopListening();
    }, this.silenceTimeoutDuration);
  }
}
